import * as React from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import CopyToClipboard from 'react-copy-to-clipboard'
import { FormInstance } from 'antd/es/form'
import { Button, Empty, Form, Input, Skeleton, Space, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { MinusCircleFilled, SyncOutlined, WarningOutlined } from '@ant-design/icons'
import { RuleObject } from 'antd/es/form'
import { Modal, Well } from '@pushly/aqe/lib/components'
import { ApiKeyDto } from '@halo/dtos/user-with-key.dto'
import { AccountService } from '@halo/services/account.service'
import { simpleNotification } from '@halo/_utils/notifications'
import './api-settings-well.scss'
import moment from 'moment-timezone'
import { AppService } from '@halo/services/app.service'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'

interface IIASWProps {
    actions?: any[]
}

export interface IApiKeyCreateDto {
    name: string
    email: string
}

const MASKED_KEY = '•'.repeat(32)

export const InternalApiSettingWell: React.FC<IIASWProps> = (_props: IIASWProps) => {
    const accountSvc = useService(AccountService)
    const appSvc = useService(AppService)
    const [apiUsers, setApiUsers] = React.useState<ApiKeyDto[] | undefined>()
    const formRef = React.createRef<FormInstance>()

    const getApiUsers = React.useCallback(async () => {
        const { data: accountApiUsers } = await accountSvc.fetchInternalApiUsers()
        setApiUsers(accountApiUsers ?? [])
    }, [accountSvc])

    const handleKeyGenerateSuccess = React.useCallback(async (newToken: ApiKeyDto) => {
        Modal.success({
            className: 'api-key-success-modal',
            title: 'New API Key',
            icon: <WarningOutlined />,
            okText: 'Got it',
            content: (
                <div className="api-key-success-modal-content">
                    <p>
                        The following API Key has been generated. Please copy and save it now — once this window is
                        closed you will not be able to retrieve it. A new one must be generated.
                    </p>
                    <CopyToClipboard
                        text={newToken?.token!}
                        onCopy={() => simpleNotification('success', 'Your API Key has been copied!')}
                    >
                        <div className="api-key-viewable">{newToken.token}</div>
                    </CopyToClipboard>
                </div>
            ),
        })
    }, [])

    const handleCreateAPIKey = React.useCallback(async () => {
        const createKey = async (dto: IApiKeyCreateDto, opts?: IApiCallOptions) => {
            const res = await appSvc.createApiKey(dto, opts)
            if (res.ok) {
                handleKeyGenerateSuccess(res.data)
                await getApiUsers()
                return { ok: res.ok }
            }
        }
        Modal.confirm({
            title: 'Create API Key',
            icon: <WarningOutlined />,
            okText: 'Create',
            onOk: async () => {
                const form = formRef.current
                if (!form) return
                const valid = await form.validateFields()
                if (!valid) return
                const createDto = {
                    name: form.getFieldValue('name'),
                    email: form.getFieldValue('email'),
                }
                const res = await createKey(createDto)
                return res?.ok ? true : false
            },
            content: (
                <Form ref={formRef} layout="vertical">
                    <p>Provide a name for the new API Key and the associated Pushly email address.</p>
                    <Form.Item
                        name="name"
                        label="Name"
                        validateTrigger={false}
                        rules={[{ required: true, message: 'Name is required.' }]}
                    >
                        <Input autoFocus={true} data-1p-ignore={true} />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            {
                                required: true,
                                validator: (_rule, value) => {
                                    if (!value?.trim()) return Promise.reject('Email is required.')
                                    if (!/.+@(pushly)\.com$/.test(value.trim())) {
                                        return Promise.reject('Email must be an "@pushly.com" address.')
                                    }
                                    return Promise.resolve(true)
                                },
                            },
                        ]}
                    >
                        <Input data-1p-ignore={true} />
                    </Form.Item>
                </Form>
            ),
        })
    }, [appSvc, formRef, getApiUsers, handleKeyGenerateSuccess])

    React.useEffect(() => {
        if (!apiUsers) getApiUsers()
    }, [apiUsers, getApiUsers])

    const loading = apiUsers === undefined

    const columns: ColumnsType<ApiKeyDto> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name: string | undefined) => name ?? '—',
        },
        {
            title: 'API Key',
            dataIndex: 'token',
            key: 'token',
            render: () => <Input size="small" value={MASKED_KEY} type="password" disabled={true} />,
        },
        {
            title: 'Last Used',
            dataIndex: 'lastUsedAt',
            key: 'lastUsedAt',
            width: 160,
            render: (lastUsedAt: string | undefined) => (lastUsedAt ? moment(lastUsedAt).format('LL') : 'N/A'),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'right',
            render: (_: unknown, apiKey: ApiKeyDto) => (
                <ApiKeyRowActions apiKey={apiKey} triggerReload={getApiUsers} handleViewableToken={handleKeyGenerateSuccess} />
            ),
        },
    ]

    return (
        <Well
            actions={
                <Button key="new-api-key" size="small" onClick={handleCreateAPIKey}>
                    Create API Key
                </Button>
            }
            title={`Internal API Key${(apiUsers?.length ?? 0) === 1 ? '' : 's'}`}
            footerMode="hidden"
            className="api-well"
            padding="none"
        >
            {loading ? (
                <Skeleton active={true} title={false} paragraph={{ rows: 3 }} />
            ) : (
                <Table<ApiKeyDto>
                    size="small"
                    bordered={false}
                    columns={columns}
                    dataSource={apiUsers}
                    rowKey={(row) => row.id ?? row.name}
                    pagination={false}
                    locale={{
                        emptyText: (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Active API Keys" />
                        ),
                    }}
                />
            )}
        </Well>
    )
}

interface IApiKeyRowActionsProps {
    apiKey: ApiKeyDto
    triggerReload: () => void
    handleViewableToken: (key: ApiKeyDto) => void
}

const ApiKeyRowActions: React.FC<IApiKeyRowActionsProps> = ({ apiKey, triggerReload, handleViewableToken }) => {
    const accountSvc = useService(AccountService)
    const appSvc = useService(AppService)
    const formRef = React.createRef<FormInstance>()
    const [isGeneratingKey, setIsGeneratingKey] = React.useState(false)

    const regenerateKey = async () => {
        if (isGeneratingKey) return
        setIsGeneratingKey(true)
        const res = await accountSvc.regenerateInternalApiKey(apiKey?.id)
        if (res.ok) {
            handleViewableToken(res.data)
            triggerReload()
        }
        setIsGeneratingKey(false)
    }

    const handleRegenClick = async () => {
        Modal.confirm({
            title: 'Regenerate API Key',
            icon: <WarningOutlined />,
            okText: 'Regenerate',
            onOk: async () => {
                const form = formRef.current
                if (!form) return
                await form.validateFields()
                await regenerateKey()
                return true
            },
            content: <ConfirmKeywordForm formRef={formRef} keyword="REGENERATE" actionWord="regenerate" />,
        })
    }

    const handleArchiveKey = async () => {
        const archive = async (apiKeyId: string, opts?: IApiCallOptions) => {
            const res = await appSvc.archiveApiKey(apiKeyId, opts)
            if (res.ok) triggerReload()
        }
        Modal.confirm({
            title: 'Archive API Key',
            icon: <WarningOutlined />,
            okText: 'Archive',
            onOk: async () => {
                const form = formRef.current
                if (!form) return
                const valid = await form.validateFields()
                if (valid) await archive(apiKey.id)
                return true
            },
            content: <ConfirmKeywordForm formRef={formRef} keyword="ARCHIVE" actionWord="archive" />,
        })
    }

    return (
        <Space size={4}>
            <Tooltip title="Regenerate Key">
                <Button size="small" icon={<SyncOutlined />} onClick={handleRegenClick} />
            </Tooltip>
            <Tooltip title="Archive Key">
                <Button size="small" icon={<MinusCircleFilled className="archive-icon" />} onClick={handleArchiveKey} />
            </Tooltip>
        </Space>
    )
}

interface IConfirmKeywordFormProps {
    formRef: React.RefObject<FormInstance>
    keyword: string
    actionWord: string
}

const ConfirmKeywordForm: React.FC<IConfirmKeywordFormProps> = ({ formRef, keyword, actionWord }) => (
    <Form ref={formRef}>
        <p>
            Are you sure you want to {actionWord} the current API token? Once {actionWord}d the current token will no
            longer be valid and any existing API calls using it will need to be updated.
        </p>
        <p>
            Please type <b>{keyword}</b> to continue with this action.
        </p>
        <Form.Item
            name="confirm"
            validateTrigger={false}
            rules={[
                {
                    validator: (_rule: RuleObject, val) => {
                        if (!val?.trim()) return Promise.reject(`${keyword} is required.`)
                        if (val !== keyword) return Promise.reject(`You must type "${keyword}" to continue.`)
                        return Promise.resolve(true)
                    },
                },
            ]}
        >
            <Input autoFocus={true} />
        </Form.Item>
    </Form>
)
